export interface WorkItem {
  title: string
  description: string
  year: string
  category: string
  slug: string
  /** Last meaningful public-page update, used by sitemap.xml. */
  updatedAt: string
  coverImage?: string
  technologies?: string[]
  demoUrl?: string
  isNew?: boolean // For projects less than 3 months old
}

export const worksData: WorkItem[] = [
  {
    title: 'Pixelmojo Brand System',
    description:
      'The identity system for the Pixelmojo product family. Radar sweeps. Vector points. Hive multiplies. One geometric grammar makes every product unmistakably itself and unmistakably ours.',
    year: '2026',
    category: 'Brand System',
    slug: '/projects/pixelmojo-brand-system',
    updatedAt: '2026-08-12',
    technologies: ['64-unit grid', 'Space Grotesk', 'Geist', 'Design tokens'],
    isNew: true,
  },
  {
    title: 'Lakbay AI',
    description:
      'AI-powered travel concierge for the Philippines with RAG-driven itinerary generation, real-time flight search, and 3 user portals serving travelers, agents, and admins.',
    year: '2026',
    category: 'AI Travel Tech',
    slug: '/projects/lakbay-ai',
    updatedAt: '2026-08-11',
    coverImage:
      'https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?w=1200&q=80',
    technologies: [
      'Next.js 16',
      'TypeScript',
      'OpenAI',
      'Supabase pgvector',
      'Amadeus API',
      'Clerk',
    ],
    isNew: true,
    demoUrl: 'https://travel-conceirge.vercel.app',
  },
  {
    title: 'Vector by Pixelmojo',
    description:
      'AI sales qualification agent that analyzes prospects across 12 dimensions in real-time. Eliminates 60% of unqualified leads while capturing every high-intent prospect 24/7.',
    year: '2025',
    category: 'AI Sales Agent',
    slug: '/projects/vector',
    updatedAt: '2026-08-12',
    coverImage: '/vector-og-image-02.png',
    technologies: [
      'Next.js 15',
      'TypeScript',
      'Supabase',
      'OpenAI',
      'PostgreSQL',
      'Vercel',
    ],
    isNew: true,
    demoUrl: '/vector',
  },
  {
    title: 'Mojo AI',
    description:
      'Portfolio concept for a Figma creative workflow that explores controlled ad variations from one master template. The interactive case study uses synthetic data and does not claim a measured production output benchmark.',
    year: '2025',
    category: 'Product Design',
    slug: '/projects/mojo-ai',
    updatedAt: '2026-08-13',
    coverImage:
      'https://epvuzbkqgx3gmgiq.public.blob.vercel-storage.com/images/project/mojo-ai-visual.webp',
    technologies: [
      'TypeScript',
      'Figma Plugin API',
      'OpenAI GPT-5',
      'shadcn/ui',
    ],
  },
  {
    title: 'SEO Intelligence Platform',
    description:
      'Built with Google Search Console MCP to monitor rankings, identify search-to-citation gaps, and generate evidence-linked content briefs from verified performance data.',
    year: '2025',
    category: 'Marketing Tech',
    slug: '/projects/seo-intelligence-platform',
    updatedAt: '2026-08-13',
    coverImage:
      'https://epvuzbkqgx3gmgiq.public.blob.vercel-storage.com/images/project/seo-intelligence-platform-visual.webp',
    technologies: [
      'TypeScript',
      'MCP Protocol',
      'Google Search Console API',
      'Claude AI',
      'Data Analytics',
    ],
    isNew: true,
  },
  {
    title: 'Real Estate Earnings Tracker',
    description:
      'Predictive analytics platform that transforms property investment data into actionable profit forecasts with real-time market analysis and portfolio optimization.',
    year: '2025',
    category: 'Real Estate Tech',
    slug: '/projects/real-estate-earnings-tracker',
    updatedAt: '2026-08-13',
    coverImage:
      'https://epvuzbkqgx3gmgiq.public.blob.vercel-storage.com/images/project/real-estate-earning-tracker-visual.webp',
    technologies: [
      'React',
      'TypeScript',
      'Python',
      'Machine Learning',
      'PostgreSQL',
      'Chart.js',
    ],
    demoUrl: '#',
  },
  {
    title: 'Logistics Track & Trace System',
    description:
      'Enterprise logistics platform that unified carrier and warehouse events into a real-time shipment record with exception tracking across every handoff.',
    year: '2025',
    category: 'Enterprise SaaS',
    slug: '/projects/logistics-track-trace-system',
    updatedAt: '2026-08-13',
    coverImage:
      'https://epvuzbkqgx3gmgiq.public.blob.vercel-storage.com/images/project/logistics-track-and-trace-visual.webp',
    technologies: [
      'React',
      'TypeScript',
      'Node.js',
      'PostgreSQL',
      'Redis',
      'WebSockets',
    ],
    demoUrl: '#',
  },
]

export const getAllWorks = () => worksData

/** CollectionPage ItemList entries, derived from the same records rendered by
 * /projects so cards and structured data cannot silently diverge. */
export const buildProjectSchemaItems = () =>
  worksData.map(work => ({
    name: work.title,
    url: work.slug,
    description: work.description,
  }))

// SEO-optimized alt text generation
// The `imageType` parameter went with the thumbnail cards: the only surviving
// caller is the case-study hero, so the wording is fixed.
export const generateAltText = (work: WorkItem): string => {
  const { title, category, technologies } = work
  const mainTech = technologies?.[0] || ''
  const imageContext = 'case study image'

  // Create descriptive alt text under 125 characters for SEO
  const altText = `${title} ${category.toLowerCase()} ${imageContext}${mainTech ? ` - ${mainTech}` : ''} by Pixelmojo`

  return altText.length > 125
    ? `${title} ${category.toLowerCase()} ${imageContext} by Pixelmojo`
    : altText
}
